import { useDeferredValue, useMemo, useState } from 'react'
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Chip,
  Group,
  LoadingOverlay,
  Pagination,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { isEmpty } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { TbCircleCheck, TbDownload, TbRefresh, TbSearch } from 'react-icons/tb'
import { getAllActiveRoutesOptions, getControlDataSchemasOptions, getRouteProblemsSummaryOptions } from '@/api/main'
import ErrorState from '@/components/Common/ErrorState'
import PageShell from '@/components/Common/PageShell'
import { SectionTitle } from '@/components/Common/SectionTitle'
import { sortControlSchemas } from '@/components/MapSnapshots/sortControlSchemas'
import { usePageTitle } from '@/hooks/usePageTitle'
import { CITY_NAME } from '@/meta'
import { useAuthStore } from '@/stores/auth'
import { formatDateTime } from '@/utils/datetime'
import { triggerBlobDownload } from '@/utils/download'
import { errorMessage } from '@/utils/other'
import { hasAnyPermission, hasPermission } from '@/utils/permissions'
import { PROBLEM_COLORS } from './problemColors'
import RouteProblemRow from './RouteProblemRow'
import {
  countRoutesByProblemType,
  filterRouteProblems,
  indexRoutes,
  paginate,
  isCsvText,
  parseRouteProblemsCsv,
  PROBLEM_TYPES,
  routeKey,
  type ProblemType,
} from './routeProblems'

const COLUMN_COUNT = 6
const PAGE_SIZE = 50

type RouteProblemsPageProps = {
  schema: string
  includeInactive: boolean
  onSchemaChange: (schema: string) => void
  onIncludeInactiveChange: (includeInactive: boolean) => void
}

export const RouteProblemsPage = ({
  schema,
  includeInactive,
  onSchemaChange,
  onIncludeInactiveChange,
}: RouteProblemsPageProps) => {
  const { t } = useTranslation('routeProblems')
  usePageTitle(`${t('title')} | ${CITY_NAME}`)

  const userPermissions = useAuthStore(state => state.userPermissions)
  const canViewRoutes = hasAnyPermission(userPermissions, 'routes')
  const canPlanRoutes = hasPermission(userPermissions, 'routes', 'write')

  const [search, setSearch] = useState('')
  const [selectedTypes, setSelectedTypes] = useState<ProblemType[]>([])
  const [page, setPage] = useState(1)

  const schemasQuery = useQuery(getControlDataSchemasOptions())
  const routesQuery = useQuery(getAllActiveRoutesOptions())
  const problemsQuery = useQuery({
    ...getRouteProblemsSummaryOptions({
      headers: { 'city-sense-control-data-schema': schema },
      query: { include_inactive: includeInactive },
      parseAs: 'text',
    }),
    placeholderData: keepPreviousData,
  })

  const csv = isCsvText(problemsQuery.data) ? problemsQuery.data : undefined
  const problems = useMemo(() => (csv === undefined ? [] : parseRouteProblemsCsv(csv)), [csv])
  const routes = useMemo(() => indexRoutes(routesQuery.data ?? []), [routesQuery.data])
  const counts = useMemo(() => countRoutesByProblemType(problems), [problems])
  // Typing stays responsive while the table re-renders with the deferred search
  const deferredSearch = useDeferredValue(search)
  const visibleProblems = useMemo(
    () => filterRouteProblems(problems, { search: deferredSearch, types: selectedTypes, routes }),
    [problems, deferredSearch, selectedTypes, routes]
  )
  const pagination = useMemo(() => paginate(visibleProblems, page, PAGE_SIZE), [visibleProblems, page])

  const schemas = useMemo(() => sortControlSchemas(schemasQuery.data ?? []), [schemasQuery.data])
  const schemaInfo = schemas.find(it => it.schema_name === schema)
  const schemaOptions = useMemo(() => {
    const names = schemas.map(it => it.schema_name)
    return names.includes(schema) ? names : [schema, ...names]
  }, [schemas, schema])

  const isRefreshing = problemsQuery.isFetching

  return (
    <PageShell
      fullWidth
      title={
        <Stack gap={4}>
          <SectionTitle order={1} mb={0} style={{ margin: 0 }}>
            {t('title')}
          </SectionTitle>
          <Group gap="xs">
            <Badge variant="light" radius="sm" tt="none" ff="monospace">
              {schema}
            </Badge>
            {schemaInfo?.map_version && (
              <Text size="sm" c="dimmed">
                {t('mapVersion', { version: schemaInfo.map_version })}
              </Text>
            )}
            {schemaInfo?.modified_at && (
              <Text size="sm" c="dimmed">
                {t('modifiedAt', { date: formatDateTime(schemaInfo.modified_at) })}
              </Text>
            )}
          </Group>
        </Stack>
      }
      action={
        <Group gap="sm" align="center">
          <Switch
            label={t('includeInactive')}
            checked={includeInactive}
            onChange={event => {
              setPage(1)
              onIncludeInactiveChange(event.currentTarget.checked)
            }}
          />
          <Select
            aria-label={t('schema')}
            w={280}
            data={schemaOptions}
            value={schema}
            onChange={value => {
              if (!value) return
              setPage(1)
              onSchemaChange(value)
            }}
            allowDeselect={false}
            searchable
            disabled={schemasQuery.isLoading}
          />
          <Tooltip label={t('downloadCsv')}>
            <ActionIcon
              variant="light"
              size="lg"
              aria-label={t('downloadCsv')}
              disabled={csv === undefined}
              onClick={() =>
                csv && triggerBlobDownload(new Blob([csv], { type: 'text/csv' }), `route-problems-${schema}.csv`)
              }
            >
              <TbDownload size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('recheck')}>
            <ActionIcon
              variant="light"
              size="lg"
              aria-label={t('recheck')}
              loading={isRefreshing}
              onClick={() => void problemsQuery.refetch()}
            >
              <TbRefresh size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      }
    >
      {problemsQuery.isError ? (
        <ErrorState
          title={t('failedToLoad')}
          message={errorMessage(problemsQuery.error, t('unableToLoad', { schema }))}
          showBackButton={false}
          retryAction={
            <Button variant="light" color="red" onClick={() => void problemsQuery.refetch()}>
              {t('retry', { ns: 'common' })}
            </Button>
          }
        />
      ) : (
        <Card withBorder padding="lg" radius="md" pos="relative">
          <LoadingOverlay visible={problemsQuery.isLoading || problemsQuery.isPlaceholderData} />
          {!problemsQuery.isLoading && isEmpty(problems) ? (
            <Alert color="teal" variant="light" icon={<TbCircleCheck size={18} />} title={t('noProblemsTitle')}>
              {t(includeInactive ? 'noProblemsIncludingInactive' : 'noProblems', { schema })}
            </Alert>
          ) : (
            <Stack gap="md">
              <Group justify="space-between" align="flex-end">
                <Chip.Group
                  multiple
                  value={selectedTypes}
                  onChange={value => {
                    setPage(1)
                    setSelectedTypes(value as ProblemType[])
                  }}
                >
                  <Group gap="xs">
                    {PROBLEM_TYPES.filter(type => counts[type] > 0).map(type => (
                      <Chip key={type} value={type} color={PROBLEM_COLORS[type]} variant="light" size="sm">
                        {t(`types.${type}.label`)} · {counts[type]}
                      </Chip>
                    ))}
                  </Group>
                </Chip.Group>
                <TextInput
                  w={260}
                  leftSection={<TbSearch size={16} />}
                  placeholder={t('searchPlaceholder')}
                  aria-label={t('searchPlaceholder')}
                  value={search}
                  onChange={event => {
                    setPage(1)
                    setSearch(event.currentTarget.value)
                  }}
                />
              </Group>

              <Text size="sm" c="dimmed">
                {t('summary', { visible: visibleProblems.length, count: problems.length })}
              </Text>

              <Table.ScrollContainer minWidth={800}>
                <Table highlightOnHover verticalSpacing="sm">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th w={40} />
                      <Table.Th>{t('columns.route')}</Table.Th>
                      <Table.Th>{t('columns.version')}</Table.Th>
                      <Table.Th>{t('columns.name')}</Table.Th>
                      <Table.Th>{t('columns.problems')}</Table.Th>
                      <Table.Th />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {isEmpty(visibleProblems) && (
                      <Table.Tr>
                        <Table.Td colSpan={COLUMN_COUNT}>
                          <Text c="dimmed" ta="center" py="md">
                            {t('noMatches')}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                    {pagination.items.map(problem => {
                      const key = routeKey(problem.routeId, problem.routeVersion)
                      return (
                        <RouteProblemRow
                          key={key}
                          problem={problem}
                          route={routes.get(key)}
                          selectedTypes={selectedTypes}
                          canViewRoutes={canViewRoutes}
                          canPlanRoutes={canPlanRoutes}
                          columnCount={COLUMN_COUNT}
                        />
                      )
                    })}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>

              {pagination.pageCount > 1 && (
                <Group justify="center">
                  <Pagination total={pagination.pageCount} value={pagination.page} onChange={setPage} />
                </Group>
              )}
            </Stack>
          )}
        </Card>
      )}
    </PageShell>
  )
}
